import _ from 'lodash';
import React, { PropTypes } from 'react';
import { graphql, compose } from 'react-apollo';
import ApolloClient from 'apollo-client';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import actions from '../../actions';
import gql from 'graphql-tag';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import {
	Icon,
	Pane,
	Alert,
	Button,
	Spinner,
	Dropdown,
	Tooltip,
} from '../../components';
import moment from 'moment';

// Import the CSS
import '../../../scss/containers/subdivisions/_verify-pane.scss';

export class VerifySubdivisionPane extends Pane {

	static initialState = {
		searchResults: [],
		suggestedMatch: null,
		showMergeSection: false,
		isLoadingSuggestedMatch: false,
		isFetchingSearchResults: false,
		isVerifyingSubdivision: false,
		isMergingSubdivision: false,
		hasAutoCompleteError: false,
		showExpansionConfirmation: false,
		errors: [],
	};

	static propTypes = {
		builderId: PropTypes.string.isRequired,
		client: PropTypes.instanceOf(ApolloClient).isRequired,
		visible: PropTypes.bool.isRequired,
		onClose: PropTypes.func.isRequired,
		listFilterType: PropTypes.string,
		subdivision: PropTypes.shape({
			id: PropTypes.string,
			uniqueId: PropTypes.string,
			name: PropTypes.string,
			number: PropTypes.string,
			isVerified: PropTypes.bool,
			deletedAt: PropTypes.string,
			sourceFeed: PropTypes.shape({
				id: PropTypes.string,
			}),
		}),
	};

	state = VerifySubdivisionPane.initialState;

	constructor (props) {
		super(props);

		this.close = this.close.bind(this);
		this.verify = this.verify.bind(this);
		this.suggestMatch = this.suggestMatch.bind(this);
		this.handleClickClose = this.handleClickClose.bind(this);
		this.handleClickMerge = this.handleClickMerge.bind(this);
		this.handleAutoComplete = this.handleAutoComplete.bind(this);
		this.handleChangeSubdivision = this.handleChangeSubdivision.bind(this);
		this.handleAcceptExpansionConfirmation = this.handleAcceptExpansionConfirmation.bind(this);
		this.handleDeclineExpansionConfirmation = this.handleDeclineExpansionConfirmation.bind(this);
		this.generateDiffs = this.generateDiffs.bind(this);
	}

	close (shouldRefetchData) {
		this.setState(VerifySubdivisionPane.initialState, () => this.props.onClose(shouldRefetchData));
	}

	async verify (e) {
		e.preventDefault();

		this.setState({ isVerifyingSubdivision: true });

		try {

			await this.props.verifySubdivision(this.props.subdivision);

			const completedText = this.props.subdivision.deletedAt ? 'deleted' : 'correct';

			this.props.actions.setGlobalMsg(
				'success',
				`${this.props.subdivision.name} has been successfully verified as ${completedText}.`
			);

			this.close(true);

		} catch (err) {
			this.setState({
				errors: ['Could not verify the community.'],
				isVerifyingSubdivision: false,
			});

		}
	}

	async suggestMatch (e) {
		e.preventDefault();

		if (this.state.showMergeSection) {
			return;
		}

		this.setState({
			showMergeSection: true,
			isLoadingSuggestedMatch: true,
		});

		try {

			const { data: { subdivisions } } = await this.props.client.query({
				query: suggestMatchQuery,
				variables: {
					limit: 1,
					filter: {
						id: { ne: this.props.subdivision.id },
						builderId: { eq: this.props.builderId },
						deletedAt: { null: !!this.props.subdivision.deletedAt },
						sourceFeedId: { eq: this.props.subdivision.sourceFeed.id },
						suggest: {
							name: this.props.subdivision.name,
							number: this.props.subdivision.number,
						},
						active: null,
					},
				},
			});

			let suggestedMatch = null;

			if (subdivisions && subdivisions.results) {
				[ suggestedMatch ] = subdivisions.results;
			}

			this.setState({ isLoadingSuggestedMatch: false, suggestedMatch });

		} catch (err) {

			this.setState({
				errors: ['Could not load a suggested community match.'],
				isLoadingSuggestedMatch: false,
			});

		}

	}

	handleAcceptExpansionConfirmation (e) {
		e.preventDefault();

		this.handleClickMerge(e, true);
		this.setState({ showExpansionConfirmation: false });
	}

	handleDeclineExpansionConfirmation (e) {
		e.preventDefault();

		this.setState({ showExpansionConfirmation: false });
	}

	async handleClickMerge (e, acceptedExpansionConfirmation = false) {
		e.preventDefault();

		// If one of the selected subdivisions has "EXP"
		// and the subdivision numbers match everything except the "EXP" part
		// and they have not accepted the Expansion Confirmation
		// then show the Expansion Confirmation.
		if ((this.props.subdivision.number.toLowerCase().endsWith('exp') ||
			this.state.suggestedMatch.number.toLowerCase().endsWith('exp')) &&
			this.props.subdivision.number.replace(/exp$/i, '') === this.state.suggestedMatch.number.replace(/exp$/i, '') &&
			!acceptedExpansionConfirmation) {
			this.setState({ showExpansionConfirmation: true });

			return;
		}

		this.setState({ isMergingSubdivision: true });

		try {

			if (this.props.subdivision.deletedAt) {
				// Merge down from the selected (added) subdivision to the current (deleted) one
				await this.props.mergeSubdivisions(this.state.suggestedMatch, this.props.subdivision);
			} else {
				// Merge down from the current (added) subdivision to the selected (deleted) one
				await this.props.mergeSubdivisions(this.props.subdivision, this.state.suggestedMatch);
			}

			// Close pane and dispatch global success message or local error alert
			this.props.actions.setGlobalMsg('success', 'The community has been successfully merged.');
			this.setState({ isMergingSubdivision: false });
			this.close(true);

		} catch (err) {
			let errors = ['Could not merge the communities together.'];

			if (err.graphQLErrors) {
				const exposedErrors = err.graphQLErrors
					.filter(e => _.get(e, 'extensions.exception.expose', false))
					.map(e => e.message);
				if (exposedErrors.length) {
					errors.push(...exposedErrors);
				}
			}

			this.setState({
				errors,
				isMergingSubdivision: false,
			});
		}
	}

	handleChangeSubdivision (option) {
		this.setState({ suggestedMatch: option.value });
	}

	async handleAutoComplete (value) {

		this.setState({ isFetchingSearchResults: true });

		try {

			const { data: { subdivisions } } = await this.props.client.query({
				query: suggestMatchQuery,
				variables: {
					limit: 5,
					filter: {
						query: `%${value}%`,
						id: { ne: this.props.subdivision.id },
						builderId: { eq: this.props.builderId },
						deletedAt: { null: !!this.props.subdivision.deletedAt },
						sourceFeedId: { eq: this.props.subdivision.sourceFeed.id },
						active: null,
					},
				}
			});

			let searchResults = [];

			if (subdivisions && subdivisions.results) {
				searchResults = subdivisions.results;
			}

			this.setState({
				hasAutoCompleteError: false,
				isFetchingSearchResults: false,
				searchResults,
			});

		} catch (err) {

			this.setState({
				searchResults: [],
				hasAutoCompleteError: true,
				isLoadingSuggestedMatch: false,
			});

		}
	}

	formatAddress (streetOne, streetTwo, city, state, zip, isHighlighted) {
		if (!streetOne && !streetTwo && !city && !state && !zip) {
			return;
		}

		return (
			<div className={ `address ${isHighlighted ? 'address--highlighted' : ''}` }>
				<If condition={ streetOne }>
					<div className="street-one">{ streetOne }</div>
				</If>
				<If condition={ streetTwo }>
					<div className="street-two">{ streetTwo }</div>
				</If>
				<If condition={ city }>
					<span className="city">{city},</span>&nbsp;
				</If>
				<If condition={ state }>
					<span className="state">{state}</span>&nbsp;
				</If>
				<If condition={ zip }>
					<span className="zip">{zip}</span>
				</If>
			</div>
		);
	}

	formatGeo (lat, lon, isLatHighlighted, isLonHighlighted) {
		return (
			<If condition={ lat || lon }>
				<div className="geo">
					<If condition={ lat }>
					<div className="coordinate-container">
						<div className="title">Lat</div>
						<div className={ `coordinate ${isLatHighlighted ? 'coordinate--highlighted' : ''}` }>{lat}</div>
					</div>
					</If>
					<If condition={ lon }>
					<div className="coordinate-container">
						<div className="title">Lon</div>
						<div className={ `coordinate ${isLonHighlighted ? 'coordinate--highlighted' : ''}` }>{lon}</div>
					</div>
					</If>
				</div>
			</If>
		);
	}

	formatSubdivisionDetails (subdivision, diffs, isMergeCandidate) {
		const subAddressDiff = diffs.subStreetOne || diffs.subStreetTwo || diffs.subCity || diffs.subState || diffs.subZip;
		const salesAddressDiff = diffs.salesStreetOne || diffs.salesStreetTwo || diffs.salesCity || diffs.salesState || diffs.salesZip;
		const phoneDiff = diffs.salesPhoneAreaCode || diffs.salesPhonePrefix || diffs.salesPhoneSuffix;

		return (<div className="subdivision-details">
					<div className={ `subdivision-details-header` }>
						<div className={ `subdivision-details-header-info ${ isMergeCandidate ? 'subdivision-details-header-info--with-merge-button' : ''}` }>
							<div className={ `name ${diffs.name ? 'name--highlighted': ''}` }>{ subdivision.name }</div>
							<div className="subtext">
								<div className={ `number ${diffs.number ? 'number--highlighted': ''}` }>{ subdivision.number }</div>
								{ subdivision.deletedAt && <div className={ `deleted-date ${diffs.deletedAt ? 'deleted-date--highlighted': ''}` }>Deleted on { moment(subdivision.deletedAt).format('l') }</div> }
							</div>
						</div>
						<If condition={ isMergeCandidate }>
							<div className="merge-button-container">
								<Button
									className="merge-button"
									size="small"
									disabled={ this.state.isMergingSubdivision || this.state.showExpansionConfirmation }
									onClick={ this.handleClickMerge }>
									{ this.state.isMergingSubdivision ? 'Merging...' : 'Merge' }
								</Button>
							</div>
						</If>
					</div>
					<ReactCSSTransitionGroup
						transitionName="subdivision-pane-alert--expansion"
						transitionEnterTimeout={ 500 }
						transitionLeaveTimeout={ 500 }>
						<If condition={ isMergeCandidate && this.state.showExpansionConfirmation }>
							<Alert className="subdivision-pane-alert subdivision-pane-alert--expansion" style="warning">
								<div>The community you are trying to merge contains "EXP" in the community number, meaning it could be an expansion community. Are you sure you would like to merge?</div>
								<div className="subdivision-pane-alert-button-container">
									<Button size="small" onClick={ this.handleAcceptExpansionConfirmation }>Yes</Button>
									<Button size="small" style="blue-border" onClick={ this.handleDeclineExpansionConfirmation }>No</Button>
								</div>
							</Alert>
						</If>
					</ReactCSSTransitionGroup>
					<div className="content">
						<If condition={ subdivision.description || subdivision.website }>
							<div className={ `description ${diffs.description ? 'description--highlighted': ''}` }>
								{ _.truncate(subdivision.description, { length: 250 }) }
							</div>
						</If>
						<If condition={ subdivision.website }>
							<div className={ `website ${diffs.website ? 'website--highlighted': ''}` }>
								<a href={ subdivision.website } target="_blank" className="website" title={ subdivision.website }>
									Visit Website
									<img className="external-link-icon" src="/assets/img/icon-external-link.svg" />
								</a>
							</div>
						</If>
						<div className="locations">
							<div className="location">
								<div className="title">Location</div>
								{ this.formatAddress(subdivision.subStreetOne, subdivision.subStreetTwo, subdivision.subCity, subdivision.subState, subdivision.subZip, subAddressDiff) }
								{ this.formatGeo(subdivision.subLatitude, subdivision.subLongitude, diffs.subLatitude, diffs.subLongitude) }
							</div>
							<div className="location">
								<div className="title">Sales Office</div>
								{ this.formatAddress(subdivision.salesStreetOne, subdivision.salesStreetTwo, subdivision.salesCity, subdivision.salesState, subdivision.salesZip, salesAddressDiff) }
								<If condition={ subdivision.salesPhoneAreaCode || subdivision.salesPhonePrefix || subdivision.salesPhoneSuffix } >
									<div className={ `phone-number ${phoneDiff ? 'phone-number--highlighted' : ''}` }>
										({ subdivision.salesPhoneAreaCode }) {subdivision.salesPhonePrefix}-{subdivision.salesPhoneSuffix}
									</div>
								</If>
								{ this.formatGeo(subdivision.salesLatitude, subdivision.salesLongitude, diffs.salesLatitude, diffs.salesLongitude) }
							</div>
						</div>
					</div>
				</div>);
	}

	formatOptions (results) {
		return results.map(subdivision => ({
			value: subdivision,
			label: (
				<div className="subdivision-option" key={ subdivision.id }>
					<div className="name">{ subdivision.name }</div>
					<div className="subtext">
						<div>{ subdivision.number }</div>
						{ subdivision.deletedAt && <div>Deleted on { moment(subdivision.deletedAt).format('l') }</div> }
					</div>
				</div>
			),
		}));
	}

	renderHeader () {
		return (
			<h5>
				<a href="#" onClick={ this.handleClickClose }><Icon type="close" /></a>
				Verify { this.props.subdivision.name }
			</h5>
		);
	}

	generateDiffs () {
		const diffs = {};
		const compareFields = ['name', 'number', 'deletedAt', 'description', 'website',
			'salesStreetOne', 'salesStreetTwo', 'salesCity', 'salesState', 'salesZip', 'salesLatitude', 'salesLongitude',
			'salesPhoneAreaCode', 'salesPhonePrefix', 'salesPhoneSuffix',
			'subStreetOne', 'subStreetTwo', 'subCity', 'subState', 'subZip', 'subLatitude', 'subLongitude'];

		if (this.state.suggestedMatch) {
			compareFields.forEach(fieldName => {
				if (this.props.subdivision[fieldName] !== this.state.suggestedMatch[fieldName]) {
					diffs[fieldName] = true;
				}
			});
		}

		return diffs;
	}

	renderContent () {
		const diffs = this.generateDiffs();

		return (
			<div className="pane-content">
				{!!this.state.errors.length && (
					<Alert className="pane-alert" style="error">
						{this.state.errors.map(error => <p>{error}</p>)}
					</Alert>
				)}

				<div className="subdivision-pane-container">
					{this.formatSubdivisionDetails(this.props.subdivision, diffs)}

					<div className="dialog">
						<span>{ this.props.subdivision.deletedAt ? 'Should this community have been deleted?' : 'Is this a new community?' }</span>
						<div className="dialog-buttons">
							<Choose>
								<When condition={ this.state.isVerifyingSubdivision }>
									<div className="pane-loading">
										<Spinner style="inline" />
										<span>Verifying...</span>
									</div>
								</When>
								<Otherwise>
									<Choose>
										<When condition={ this.props.subdivision.isVerified }>
											<Tooltip style="style" position="left" text={ `This community has already been verified as ${ this.props.subdivision.deletedAt ? 'deleted' : 'correct' }.` }>
												<Button size="small" onClick={ () => {} }>Yes</Button>
											</Tooltip>
										</When>
										<Otherwise>
											<Button
												size="small"
												style="blue-border"
												onClick={ this.verify }>
												Yes
											</Button>
										</Otherwise>
									</Choose>
									<Button
										size="small"
										style="blue-border"
										onClick={ this.suggestMatch }>
										No
									</Button>
								</Otherwise>
							</Choose>
						</div>
					</div>

					<If condition={ this.state.showMergeSection }>
						<h6>Suggested Match</h6>

						<Choose>
							<When condition={ this.state.isLoadingSuggestedMatch }>
								<div className="pane-loading">
									<Spinner style="inline" />
									<span>Searching for a suggested match...</span>
								</div>
							</When>

							<Otherwise>

								<div className="suggestions">
									<Choose>
										<When condition={ !this.state.suggestedMatch }>
											No matching communities were found.
										</When>

										<Otherwise>
											{this.formatSubdivisionDetails(this.state.suggestedMatch, diffs, true)}
										</Otherwise>
									</Choose>
								</div>

								<div className="subdivision-auto-complete">
									<Dropdown
										wrap
										name="subdivision-auto-complete"
										error={ this.state.hasAutoCompleteError }
										isFetchingOptions={ this.state.isFetchingSearchResults }
										autoComplete={ this.handleAutoComplete }
										onChange={ this.handleChangeSubdivision }
										emptyLabel={ <span className="placeholder">Search for the matching community...</span> }
										options={ this.formatOptions(this.state.searchResults) } />
								</div>
							</Otherwise>

						</Choose>

					</If>

				</div>
			</div>
		);

	}

}

export const suggestMatchQuery = gql`
	query suggestMatch($filter: SubdivisionFilters, $limit: Int) {
		subdivisions(filter: $filter, limit: $limit) {
			results {
				id
				name
				number
				isVerified
				isFeedEnabled
				uniqueId
				customerId
				deletedAt
				description
				website
				salesStreetOne
				salesStreetTwo
				salesCity
				salesState
				salesZip
				salesLatitude
				salesLongitude
				salesPhoneAreaCode
				salesPhonePrefix
				salesPhoneSuffix
				subStreetOne
				subStreetTwo
				subCity
				subState
				subZip
				subLatitude
				subLongitude
			}
		}
	}
`;

export const verifySubdivisionMutation = gql`
	mutation verifySubdivision($id: EncodedID!) {
		updateSubdivision (input: {id: $id, isVerified: true}) {
			id
			deletedAt
			isFeedEnabled
		}
	}
`;

export const mergeSubdivisionsMutation = gql`
	mutation mergeSubdivisions($fromId: EncodedID!, $toId: EncodedID!) {
		mergeSubdivisions (fromId: $fromId, toId: $toId) {
			id
		}
	}
`;

export default compose(
	connect(
		state => state,
		dispatch => ({
			actions: {
				setGlobalMsg: bindActionCreators(actions.globalMsg.setGlobalMsg, dispatch),
			},
		})
	),
	graphql(verifySubdivisionMutation, {
		props: ({ mutate }) => ({
			verifySubdivision: subdivision => mutate({
				variables: { id: subdivision.id },
				updateQueries: {
					// Update the builder-level subdivision counts
					fetchBuilder: (prev, { mutationResult }) => {

						const subdivision = mutationResult.data.updateSubdivision;

						let { active, enabled, unverified } = prev.builder.subdivisionCounts;

						if (subdivision.deletedAt) {
							// In verifying a unverified deleted subdivision, it no longer becomes active
							active--;

							// The paid subdivision is now inactive and no longer included in the enabled count
							if (subdivision.isFeedEnabled) {
								enabled--;
							}
						} else {
							// In verifying an unverified added subdivision, it becomes active
							active++;

							// The paid subdivision is now active and included in the enabled count
							if (subdivision.isFeedEnabled) {
								enabled++;
							}
						}

						unverified--;

						return {
							...prev,
							builder: {
								...prev.builder,
								subdivisionCounts: {
									...prev.builder.subdivisionCounts,
									unverified,
									enabled,
									active,
								},
							},
						};

					},
				},
			}),
		}),
	}),
	graphql(mergeSubdivisionsMutation, {
		props: ({ mutate }) => ({
			mergeSubdivisions: (fromSubdivision, toSubdivision) => mutate({
				variables: {
					fromId: fromSubdivision.id,
					toId: toSubdivision.id,
				},
				updateQueries: {
					fetchBuilder: prev => {

						let active = prev.builder.subdivisionCounts.active;
						let unverified = prev.builder.subdivisionCounts.unverified;
						let enabled = prev.builder.subdivisionCounts.enabled;

						const areBothInactive = fromSubdivision.isVerified && !toSubdivision.isVerified;
						const areBothVerified = fromSubdivision.isVerified && toSubdivision.isVerified;

						// Only update the enabled count when merging two inactive or already verified subdivisions
						if (areBothVerified || areBothInactive) {
							if (!toSubdivision.isFeedEnabled && fromSubdivision.isFeedEnabled) {
								enabled++;
							}
							if (toSubdivision.isFeedEnabled && !fromSubdivision.isFeedEnabled) {
								enabled--;
							}
						}

						// Update builder query subdivision active and unverified counts based on previous subdivision flags
						if (!fromSubdivision.isVerified) {
							active--;
							unverified--;
						}
						if (!toSubdivision.isVerified) {
							active++;
							unverified--;
						}

						return {
							builder: {
								...prev.builder,
								subdivisionCounts: {
									...prev.builder.subdivisionCounts,
									unverified,
									enabled,
									active,
								},
							},
						};

					},
				},
			}),
		}),
	}),
)(VerifySubdivisionPane);



// WEBPACK FOOTER //
// ./src/assets/js/containers/subdivisions/VerifyPane.js